import clsx from 'clsx';
import React from 'react';

import { useDebouncedFlush, useEventCallback, useScrollListener } from '@gitbook/util-react-hooks';

interface DragToSelectProps {
    disabled: boolean;
    onSelectionStart?: () => void;
    onSelectionChange?: (rect: DOMRect) => void;
    onSelectionEnd?: () => void;
    onUnselect: () => void;
    scrollRef: React.RefObject<HTMLElement>;
    /**
     * Selection will only be triggered by events that pass the test implemented by the provided function.
     * @param event
     */
    selectionFilter?: (event: PointerEvent) => boolean;
}

const MIN_SELECT_MAGNITUDE = 50;
const SELECTION_DEBOUNCE_RATE = 10;

export function DragToSelect(props: DragToSelectProps) {
    const {
        disabled = false,
        onSelectionStart,
        onSelectionChange,
        onSelectionEnd,
        onUnselect,
        scrollRef,
        selectionFilter,
    } = props;
    const selectionContainerRef = React.useRef<HTMLDivElement>(null);
    const [scrollVector, setScrollVector] = React.useState<Vector2 | null>(null);
    const [dragVector, setDragVector] = React.useState<Vector2 | null>(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const debouncedOnSelectionChange = useDebouncedFlush(SELECTION_DEBOUNCE_RATE);

    const onPointerDown = useEventCallback((event) => {
        const shouldFilterOutEvent = selectionFilter ? !selectionFilter(event) : false;
        if (shouldFilterOutEvent) {
            return;
        }

        if (!isPrimaryButtonPressed(event)) {
            return;
        }

        if (!isDragging) {
            onUnselect();
        }

        if (!selectionContainerRef.current) {
            return;
        }

        // stop propagation but don't prevent default to allow focus to move to editor
        event.stopPropagation();

        selectionContainerRef.current.setPointerCapture(event.pointerId);
        const rectangle = selectionContainerRef.current.getBoundingClientRect();
        const x = event.clientX - rectangle.x;
        const y = event.clientY - rectangle.y;
        setDragVector([x, y, 0, 0]);

        const scrollLeft = scrollRef.current?.scrollLeft ?? 0;
        const scrollTop = scrollRef.current?.scrollTop ?? 0;
        setScrollVector([scrollLeft, scrollTop, 0, 0]);
    });

    const onPointerMove = useEventCallback((event: React.PointerEvent) => {
        if (!selectionContainerRef.current) {
            return;
        }

        if (dragVector === null || scrollVector === null) {
            return;
        }

        if (!isPrimaryButtonPressed(event)) {
            onPointerCancel(event);
            onUnselect();
            return;
        }

        event.preventDefault();
        const rectangle = selectionContainerRef.current.getBoundingClientRect();
        const x = event.clientX - rectangle.x;
        const y = event.clientY - rectangle.y;
        const [vx, vy] = dragVector;
        const nextDragVector: Vector2 = [vx, vy, x - vx, y - vy];
        setDragVector(nextDragVector);
        if (!isDragging && getMagnitude(nextDragVector) < MIN_SELECT_MAGNITUDE) {
            return;
        }
        if (!isDragging) {
            onSelectionStart?.();
        }
        setIsDragging(true);
        debouncedOnSelectionChange(() =>
            onSelectionChange?.(
                pointToDOMRect([
                    nextDragVector[0] - scrollVector[2],
                    nextDragVector[1] - scrollVector[3],
                    nextDragVector[2] + scrollVector[2],
                    nextDragVector[3] + scrollVector[3],
                ])
            )
        );
    });

    const onPointerCancel = useEventCallback((event) => {
        if (!selectionContainerRef.current) {
            return;
        }
        event.preventDefault();
        selectionContainerRef.current.releasePointerCapture(event.pointerId);
        setDragVector(null);
        setScrollVector(null);
        if (isDragging) {
            onSelectionEnd?.();
        }
        setIsDragging(false);
    });

    const onWheel = useEventCallback((event: React.WheelEvent) => {
        if (isDragging) {
            scrollRef.current?.scrollBy({ top: event.deltaY });
        }
    });
    /**
     * Listen for pointer events on the document to ensure that we capture pointer events.
     * The selection container will only start to receive pointer events when there is a selection rectangle.
     */
    React.useEffect(() => {
        window.document.addEventListener('pointerdown', onPointerDown);
        return () => {
            window.document.removeEventListener('pointerdown', onPointerDown);
        };
    }, [onPointerDown]);

    const onScroll = useEventCallback(() => {
        const element = scrollRef.current;
        if (element) {
            if (scrollVector === null || dragVector === null) {
                return;
            }
            const { scrollLeft, scrollTop } = element;
            const [vx, vy] = scrollVector;
            const nextScrollVector: Vector2 = [vx, vy, scrollLeft - vx, scrollTop - vy];
            setScrollVector(nextScrollVector);
        }
    });

    useScrollListener(onScroll, scrollRef);

    React.useEffect(() => {
        if (!isDragging) {
            return;
        }

        let raf = requestAnimationFrame(scrollWhileSelecting);
        let lastTimestamp = performance.now();
        function scrollWhileSelecting(timestamp: number) {
            if (dragVector === null) {
                return;
            }
            const fps = 60;
            const frameInterval = 1000 / fps;
            const timeDelta = timestamp - lastTimestamp;
            const timeDeltaPercentage = timeDelta / frameInterval;
            lastTimestamp = timestamp;

            const point = new DOMPoint(
                dragVector[0] + dragVector[2],
                dragVector[1] + dragVector[3]
            );

            const shouldScrollUp = point.y < 50;
            const shouldScrollDown = point.y > window.innerHeight - 50;

            scrollRef.current?.scrollBy({
                top: shouldScrollDown
                    ? 10 * timeDeltaPercentage
                    : shouldScrollUp
                      ? -10 * timeDeltaPercentage
                      : 0,
            });

            raf = requestAnimationFrame(scrollWhileSelecting);
        }

        return () => cancelAnimationFrame(raf);
    }, [scrollRef, isDragging, dragVector]);

    const selectionRectangle =
        dragVector && scrollVector
            ? pointToDOMRect([
                  dragVector[0] - scrollVector[2],
                  dragVector[1] - scrollVector[3],
                  dragVector[2] + scrollVector[2],
                  dragVector[3] + scrollVector[3],
              ])
            : null;

    return disabled ? null : (
        <div
            className={clsx(
                dragVector ? 'pointer-events-auto' : 'pointer-events-none',
                'fixed inset-0 overflow-hidden bg-transparent'
            )}
            onPointerCancel={onPointerCancel}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerCancel}
            onWheel={onWheel}
            ref={selectionContainerRef}
        >
            {selectionRectangle ? (
                <div
                    className={clsx(
                        'pointer-events-none absolute isolate border-none [background:color-mix(in_srgb,_Highlight_33%,_transparent)]',
                        isDragging ? 'opacity-100' : 'opacity-50'
                    )}
                    style={{
                        willChange: 'transform',
                        height: 100,
                        transformOrigin: '0 0',
                        transform: `matrix(0,${selectionRectangle.height / 100},${selectionRectangle.width / 100},0,${selectionRectangle.left},${selectionRectangle.top})`,
                        width: 100,
                    }}
                />
            ) : null}
        </div>
    );
}

/** A very simple 2D Vector implementation with point and magnitude values */
type Vector2 = [number, number, number, number];

function getMagnitude([_x, _y, magX, magY]: Vector2) {
    return Math.sqrt(magX ** 2 + magY ** 2);
}

function pointToDOMRect([x, y, magX, magY]: Vector2) {
    return new DOMRect(
        Math.min(x, x + magX),
        Math.min(y, y + magY),
        Math.abs(magX),
        Math.abs(magY)
    );
}

export function rectanglesIntersect(rectA: DOMRect, rectB: DOMRect) {
    if (rectA.right < rectB.left || rectB.right < rectA.left) {
        return false;
    }
    if (rectA.bottom < rectB.top || rectB.bottom < rectA.top) {
        return false;
    }
    return true;
}

export function isPrimaryButtonPressed(event: React.MouseEvent | PointerEvent) {
    return (event.buttons & 1) === 1;
}
